import { useState } from "react";
import { EmptyState } from "@/shared/components/ui/EmptyState";
import { ErrorState } from "@/shared/components/ui/ErrorState";
import { InfoRow } from "@/shared/components/ui/InfoRow";
import { InitialsAvatar } from "@/shared/components/ui/InitialsAvatar";
import { ItemList } from "@/shared/components/ui/ItemList";
import { ListSearch } from "@/shared/components/ui/ListSearch";
import { LoadingSpinner } from "@/shared/components/ui/LoadingSpinner";
import { Section } from "@/shared/components/ui/Section";
import { formatBusinessDate } from "@/shared/utils/formatters";
import type { ConversationWithClient } from "../types/conversationTypes";
import type { Message } from "../types/messageTypes";

export function ConversationsList({
  conversations,
  messages = [],
  timeZone,
  isLoading,
  error,
  onRetry,
  onConversationClick,
}: {
  conversations: ConversationWithClient[];
  messages?: Message[];
  timeZone: string;
  isLoading?: boolean;
  error?: Error | null;
  onRetry: () => void;
  onConversationClick: (conversation: ConversationWithClient) => void;
}) {
  const [search, setSearch] = useState("");
  const query = search.trim().toLocaleLowerCase();
  const latestMessages = new Map<string, Message>();
  for (const message of messages) {
    if (!message.conversationId || message.deletedAt) continue;
    const previous = latestMessages.get(message.conversationId);
    if (!previous || message.createdAt > previous.createdAt) {
      latestMessages.set(message.conversationId, message);
    }
  }

  const customerName = (conversation: ConversationWithClient) =>
    [conversation.client?.firstName, conversation.client?.lastName].filter(Boolean).join(" ").trim()
    || conversation.externalNumber || "Unknown customer";
  const lastActivity = (conversation: ConversationWithClient) => {
    const previewAt = latestMessages.get(conversation.id)?.createdAt;
    const recordedAt = conversation.lastMessageAt;
    return previewAt && (!recordedAt || previewAt > recordedAt) ? previewAt : recordedAt;
  };
  const matches = conversations
    .filter((conversation) =>
      `${customerName(conversation)} ${conversation.client?.email || ""}`.toLocaleLowerCase().includes(query))
    .sort((a, b) => (lastActivity(b)?.getTime() || 0) - (lastActivity(a)?.getTime() || 0));

  return (
    <div className="space-y-[26px]">
      <ListSearch
        label="Search conversations"
        placeholder="Customer name or email"
        value={search}
        onChange={setSearch}
      />
      {error ? <ErrorState title="Couldn't load conversations" message={error.message} onRetry={onRetry} /> :
        isLoading ? <LoadingSpinner message="Loading conversations..." /> :
        conversations.length === 0 ? (
          <EmptyState title="No conversations yet" message="Existing customer conversations will appear here." />
        ) : (
          <Section title="Conversations" count={`${matches.length} conversation${matches.length === 1 ? "" : "s"}`}>
            {matches.length === 0 ? (
              <EmptyState title="No conversations match" message="Try another customer name or email." />
            ) : (
              <ItemList
                items={matches}
                renderItem={(conversation) => {
                  const lastMessage = latestMessages.get(conversation.id);
                  const lastMessageAt = lastActivity(conversation);
                  return (
                    <InfoRow
                      title={customerName(conversation)}
                      subtitle={lastMessage?.body || conversation.client?.email || undefined}
                      caption={lastMessageAt ? formatBusinessDate(lastMessageAt, timeZone, {
                        day: "numeric", month: "short", year: "numeric",
                        hour: "2-digit", minute: "2-digit",
                      }) : undefined}
                      icon={<InitialsAvatar firstName={conversation.client?.firstName || "?"} lastName={conversation.client?.lastName || ""} />}
                      searchText={search.trim()}
                      onClick={() => onConversationClick(conversation)}
                    />
                  );
                }}
              />
            )}
          </Section>
        )}
    </div>
  );
}
